import { yupResolver } from '@hookform/resolvers/yup';
import FormDatetime from 'components/desktop/shared/form-datetime';
import FormSelect from 'components/desktop/shared/form-select';
import Modal from 'components/desktop/shared/modal';
import GraphQLUploadDocument from 'components/desktop/shared/upload-document/graphql-upload-document';
import {
  IconBed,
  IconLoading,
  IconMapMarkerOutline,
  IconShower,
  IconTagOutline,
  IconVectorSquareClose,
} from 'components/icons';
import FormTextArea from 'components/shared/form-text-area';
import FormTextInput from 'components/shared/form-text-input';
import { GuestReportForm, guestReportFormDefaultValues, guestReportFormSchema } from 'forms';
import {
  CREATE_GUEST_REPORT_ATMNHA,
  CreateGuestReportATMNhaData,
  CreateGuestReportATMNhaVariables,
} from 'graphql/main/mutations';
import { FileWithFullUrls } from 'graphql/main/queries';
import { useFlexMutation, useTranslation } from 'hooks';
import {
  filter,
  includes,
  isEmpty,
  isEqual,
  isNil,
  isNumber,
  map,
  some,
  toNumber,
  toString,
} from 'lodash';
import moment from 'moment-timezone';
import { DetailPageContext } from 'pages/[ket-qua-tim-kiem]/[chi-tiet-tin-rao]';
import { useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { convertNumberToVietnameseCurrencyShortString, getListYears } from 'utils';

interface Props {
  onClose?: () => void;
  onComplete?: () => void;
  onError?: () => void;
}

const GuestReportModal = ({ onClose, onComplete, onError }: Props) => {
  const { realEstate } = useContext(DetailPageContext).pageData ?? {};
  const {
    id: realEstateId,
    postTitle,
    mediaUrls,
    isForSell,
    postType,
    isAuthorizedRe,
    isForeclosure,
    fullAddress,
    area,
    type,
    leadREStatus,
    isLeadRE,
    bedroomCount,
    toiletCount,
    price,
  } = realEstate ?? {};
  const translation = useTranslation();
  const form = useForm<GuestReportForm>({
    mode: 'onChange',
    defaultValues: guestReportFormDefaultValues,
    resolver: yupResolver(guestReportFormSchema),
  });
  const { formState, setFocus } = form;
  const { errors } = formState;
  const image = filter(mediaUrls, (media) => isEqual('image', media.type))?.[0];
  const stack = [
    {
      icon: <IconVectorSquareClose className='h-[24px] w-[24px] text-text2' />,
      content: area?.toLocaleString(),
      suffix: 'm\u00B2',
    },
    {
      icon: <IconShower className='h-[24px] w-[24px] text-text2' />,
      content: toiletCount?.toLocaleString(),
      suffix: 'nhà vệ sinh',
    },
    {
      icon: <IconBed className='h-[24px] w-[24px] text-text2' />,
      content: bedroomCount?.toLocaleString(),
      suffix: 'phòng ngủ',
    },
  ];
  const [createGuestReportATMNha, { loading: loadingCreateGuestReportATMNha }] = useFlexMutation<
    CreateGuestReportATMNhaData,
    CreateGuestReportATMNhaVariables
  >('main', CREATE_GUEST_REPORT_ATMNHA);

  const birthYearOption = map(getListYears(1900, moment().year() + 100), (year) => ({
    label: toString(year),
    value: toString(year),
  }));

  const submit = async (data: GuestReportForm) => {
    const {
      fullName,
      phone,
      email,
      address,
      attachments,
      description,
      idNumber,
      time,
      yearOfBirth,
    } = data;
    if (realEstateId) {
      const { errors } = await createGuestReportATMNha({
        variables: {
          input: {
            realEstateId,
            address,
            description,
            email,
            yearOfBirth,
            time,
            fullName: toString(fullName),
            idNumber: toString(idNumber),
            phone: toString(phone),
            attachments: !isEmpty(attachments) ? attachments : undefined,
          },
        },
        onCompleted: (data) => {
          if (data.createGuestReportATMNha?.success) {
            onComplete?.();
            onClose?.();
          } else {
            onError?.();
          }
        },
        onError: () => onError?.(),
      });
      if (errors) {
        onError?.();
      }
    }
  };
  const cancelModal = () => {
    form.reset();
    onClose?.();
  };
  useEffect(() => {
    const errorElements = document.getElementsByClassName('error-message');
    if (errorElements) {
      errorElements[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errors]);

  return (
    <Modal closable title='Báo cáo dẫn khách' onClose={cancelModal}>
      <FormProvider {...form}>
        <div className='mx-[-24px] mt-[-8px] max-h-[calc(100vh-200px)] space-y-[16px] flex flex-col pt-[12px] bg-background overflow-y-auto'>
          <div className='flex space-x-[16px]'>
            <div className='flex w-[424px] flex-col rounded-[8px] bg-paper overflow-hidden'>
              <div className='relative h-[300px]'>
                <img
                  src={image ? (image as FileWithFullUrls).originalUrl : '/images/error-image.svg'}
                  alt={postTitle}
                  loading='lazy'
                  className='h-full w-full object-cover'
                />
                <div className='absolute left-[12px] top-[12px] z-[1] flex space-x-[8px]'>
                  {!isNil(isForSell) && (
                    <span
                      className={`rounded-[4px] px-[8px] py-[4px] text-[11px] font-[500] uppercase leading-[14px] text-paper ${
                        isForSell ? 'bg-accent-sale' : 'bg-accent-rent'
                      }`}
                    >
                      {isForSell ? 'bán' : 'thuê'}
                    </span>
                  )}
                  {(isAuthorizedRe || isForeclosure || isLeadRE) && (
                    <span className='rounded-[4px] bg-accent-sale px-[8px] py-[4px] text-[12px] font-[500] uppercase leading-[15px] text-paper'>
                      {isAuthorizedRe ? 'ký gửi' : isLeadRE ? 'đầu chủ' : 'phát mãi'}
                    </span>
                  )}
                  {isLeadRE && leadREStatus && (
                    <span className='rounded-[4px] bg-accent-sale px-[8px] py-[4px] text-[12px] font-[500] uppercase leading-[15px] text-paper'>
                      {translation.major.leadRealEstateStatus[leadREStatus]}
                    </span>
                  )}
                  {includes([1, 2], postType) && (
                    <span className='rounded-[4px] bg-accent-sale px-[8px] py-[4px] text-[11px] font-[500] uppercase leading-[14px] text-paper'>
                      {isEqual(postType, 1) ? 'vip đặc biệt' : 'vip 1'}
                    </span>
                  )}
                </div>
                <div className='absolute right-[6px] top-[12px] flex items-center space-x-[8px] text-paper'>
                  <span className='rounded-[50px] bg-[#000000]/[.5] px-[10px] py-[4px] text-[12px] leading-[15px]'>
                    {
                      (isForSell
                        ? translation.major.forSellRealEstateType
                        : (translation.major.forRentRealEstateType as any))[toString(type)]
                    }
                  </span>
                </div>
              </div>
              <div className='flex flex-col space-y-[24px] p-[16px]'>
                {postTitle && (
                  <span className='line-clamp-3 text-[18px] leading-[23px] font-[700]'>
                    {postTitle}
                  </span>
                )}
                {some(stack, (item) => item.content) && (
                  <div className='m-[-8px] flex max-h-[80px] flex-wrap items-center overflow-hidden'>
                    {map(
                      stack,
                      (stackItem, stackItemIndex) =>
                        stackItem.content && (
                          <div
                            key={stackItemIndex}
                            className='flex items-center space-x-[8px] whitespace-nowrap p-[8px]'
                          >
                            {stackItem.icon}
                            <span>
                              {stackItem.content} {stackItem.suffix}
                            </span>
                          </div>
                        ),
                    )}
                  </div>
                )}
                {fullAddress && (
                  <div className='flex items-center space-x-[6px]'>
                    <IconMapMarkerOutline className='mt-[1px] h-[24px] min-w-[24px] text-text2' />
                    <span className='line-clamp-2'>{fullAddress}</span>
                  </div>
                )}
                <div className='flex justify-between items-center'>
                  <div className='w-full flex items-center space-x-[10px]'>
                    <IconTagOutline className='h-[20px] min-w-[20px] text-alert2' />
                    <span className='text-[16px] font-[600] leading-[16px] text-alert2'>
                      {isNil(price) || isEqual(toNumber(price), 0)
                        ? 'Thương lượng'
                        : `${convertNumberToVietnameseCurrencyShortString(toNumber(price))}${
                            isForSell ? '' : '/tháng'
                          }`}
                    </span>
                  </div>
                  <span className='w-full truncate text-right text-[14px] leading-[16px]'>
                    {!isForSell ||
                    isNil(price) ||
                    !isNumber(toNumber(price)) ||
                    !toNumber(price) ||
                    isNil(area)
                      ? null
                      : `${convertNumberToVietnameseCurrencyShortString(
                          toNumber(price) / toNumber(area),
                        )}/m\u00B2`}
                  </span>
                </div>
              </div>
            </div>
            <div
              className='flex w-[424px] flex-col justify-between space-y-[28px] bg-paper p-[16px] rounded-[8px]'
              onSubmit={form.handleSubmit(submit)}
            >
              <div className='flex flex-col space-y-[28px]'>
                <FormTextInput
                  maxLength={118}
                  label='Họ và tên khách hàng'
                  required
                  name='fullName'
                  autoComplete='off'
                  error={form.formState.errors.fullName}
                />
                <FormTextInput
                  label='Số điện thoại'
                  required
                  name='phone'
                  maxLength={10}
                  autoComplete='off'
                  error={form.formState.errors.phone}
                />
                <FormTextInput
                  label='CMND/CCCD'
                  required
                  name='idNumber'
                  maxLength={12}
                  autoComplete='off'
                  error={form.formState.errors.idNumber}
                />
                <FormSelect
                  name='yearOfBirth'
                  options={birthYearOption}
                  label='Năm sinh'
                  error={form.formState.errors.yearOfBirth}
                  emptyOptionsText='Không có tuỳ chọn'
                  wrapperClassName='h-[40px] px-[12px] py-[9px]'
                  optionClassName='h-[32px] px-[12px]'
                  popoverClassName='py-[9px]'
                />
                <FormTextInput
                  maxLength={118}
                  label='Địa chỉ'
                  name='address'
                  autoComplete='off'
                  error={form.formState.errors.address}
                />
                <FormTextInput
                  label='Email'
                  maxLength={68}
                  name='email'
                  autoComplete='off'
                  error={form.formState.errors.email}
                />
                <FormDatetime
                  name='time'
                  label='Thời gian dẫn khách'
                  error={form.formState.errors.time}
                />
                <FormTextArea
                  label='Ghi chú'
                  name='description'
                  autoComplete='off'
                  rows={4}
                  maxLength={888}
                  error={form.formState.errors.description}
                />
              </div>
            </div>
          </div>
          <div className='p-[24px] bg-paper'>
            <GraphQLUploadDocument field='attachments' maxFiles={6} />
          </div>
        </div>
        <div className='mx-[-24px] mb-[-24px] flex justify-end border-t border-stroke px-[16px] py-[8px] space-x-[16px] bg-paper'>
          <button
            type='button'
            className='h-[38px] min-w-[140px] rounded-[8px] bg-background font-[600] duration-[200ms] hover:bg-stroke'
            onClick={onClose}
          >
            Đóng
          </button>
          <button
            disabled={loadingCreateGuestReportATMNha}
            className='flex h-[40px] w-[147px] items-center justify-center space-x-[6px] rounded-[8px] bg-primary text-paper transition duration-[200ms] ease-in-out hover:bg-primary-light disabled:opacity-50 disabled:hover:bg-primary'
            onClick={form.handleSubmit(submit)}
          >
            {loadingCreateGuestReportATMNha && <IconLoading className='animate-spin' />}
            <span className='font-[600]'>Gửi báo cáo</span>
          </button>
        </div>
      </FormProvider>
    </Modal>
  );
};

export default GuestReportModal;
